// alert wrapper component to display global alerts
import React from 'react';
import { Alert, AlertTitle, Paper, Button, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Notification } from '@fsp/types';
import { useNotifications } from '../contexts/NotificationsContext';

interface PageAlertProps {
  severity?: 'error' | 'warning' | 'info' | 'success';
  title?: string;
  message?: string;
  notification?: Notification;
}

export const PageAlert: React.FC<PageAlertProps> = ({ severity, title, message, notification }) => {
  const navigate = useNavigate();
  const { dismissNotification } = useNotifications();

  // Use notification data if provided, otherwise use props
  const alertSeverity = notification?.severity || severity;
  const alertMessage = notification?.message || message;

  if (!alertSeverity || !alertMessage) {
    return null;
  }

  const handleActionClick = async () => {
    if (notification?.actionUrl) {
      // Dismiss notification first
      await dismissNotification(notification.id);
      // Navigate to action URL
      navigate(notification.actionUrl);
    }
  };

  const handleDismiss = async () => {
    if (notification) {
      await dismissNotification(notification.id);
    }
  };

  return (
    <Paper sx={{ mb: 2, p: 0 }}>
      <Alert
        severity={alertSeverity}
        variant="filled"
        onClose={notification ? handleDismiss : undefined}
        action={
          notification?.actionUrl && notification?.actionText ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                color="inherit"
                size="small"
                onClick={handleActionClick}
                variant="outlined"
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.8)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                {notification.actionText}
              </Button>
            </Box>
          ) : undefined
        }
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        {alertMessage}
      </Alert>
    </Paper>
  );
};

export default PageAlert;
